import { apiClient } from '@/api/client'
import { ApiError, parseProblemDetails } from '@/api/problemDetails'
import { isUserResponse, type UserResponse } from '@/api/responses'
import type { components } from '@/api/schema'

export type RegisterPayload = components['schemas']['RegisterRequest']
export type LoginPayload = components['schemas']['LoginRequest']
export type ForgotPasswordPayload = components['schemas']['ForgotPasswordRequest']
export type ResetPasswordPayload = components['schemas']['ResetPasswordRequest']
export type ChangePasswordPayload = components['schemas']['ChangePasswordRequest']

interface RawResult {
  readonly data?: unknown
  readonly error?: unknown
  readonly response: Response
}

function readUser(result: RawResult): UserResponse {
  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  const body: unknown = result.data

  if (!isUserResponse(body)) {
    throw new ApiError({
      title: 'A resposta da Api nao tem o formato esperado.',
      status: result.response.status,
      errorCode: 'internal_error',
      correlationId: '',
    })
  }

  return body
}

/** Retorna o usuario autenticado ou `null` quando nao ha sessao (401), sem lancar erro. */
export async function fetchCurrentUser(): Promise<UserResponse | null> {
  const result = await apiClient.GET('/api/v1/auth/me')

  if (result.response.status === 401) {
    return null
  }

  return readUser(result)
}

export async function register(payload: RegisterPayload): Promise<UserResponse> {
  const result = await apiClient.POST('/api/v1/auth/register', { body: payload })
  return readUser(result)
}

export async function login(payload: LoginPayload): Promise<UserResponse> {
  const result = await apiClient.POST('/api/v1/auth/login', { body: payload })
  return readUser(result)
}

export async function logout(): Promise<void> {
  const result = await apiClient.POST('/api/v1/auth/logout')

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
}

/**
 * Solicita o e-mail de redefinicao. A Api responde 202 exista ou nao a conta (anti-enumeracao); so
 * lanca erro em rate limit / requisicao malformada. A UI mostra sempre a mesma confirmacao.
 */
export async function forgotPassword(payload: ForgotPasswordPayload): Promise<void> {
  const result = await apiClient.POST('/api/v1/auth/password/forgot', { body: payload })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
}

/** Redefine a senha a partir do link (uid + token). 204 em sucesso; erro com detalhes de validacao. */
export async function resetPassword(payload: ResetPasswordPayload): Promise<void> {
  const result = await apiClient.POST('/api/v1/auth/password/reset', { body: payload })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
}

/** Troca a senha do usuario autenticado. 204 em sucesso; erro com detalhes de validacao. */
export async function changePassword(payload: ChangePasswordPayload): Promise<void> {
  const result = await apiClient.POST('/api/v1/auth/password/change', { body: payload })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
}
